(function () {
  'use strict';

  var ROOT_SELECTOR = '[data-aquaphoria-v5-3d]';
  var audioByRoot = new WeakMap();

  function dispatchMode(root, mode) {
    root.dataset.aquaphoriaV5Mode = mode;
    root.dispatchEvent(new CustomEvent('aquaphoria:v5-mode', {
      bubbles: false,
      detail: { mode: mode }
    }));
  }

  function dispatchFocus(root, focus) {
    root.dataset.aquaphoriaV5Focus = focus || '';
    root.dispatchEvent(new CustomEvent('aquaphoria:v5-focus', {
      bubbles: false,
      detail: { focus: focus || null }
    }));
  }

  function syncModeButton(root, button) {
    if (!button) return;
    var mode = root.dataset.aquaphoriaV5Mode === 'night' ? 'night' : 'day';
    button.setAttribute('aria-pressed', String(mode === 'night'));
    var label = button.querySelector('[data-aquaphoria-v5-mode-label]');
    if (label) label.textContent = mode === 'night' ? 'Night mode' : 'Day mode';
  }

  function stopAmbient(root) {
    var audio = audioByRoot.get(root);
    if (!audio) return;

    audioByRoot.delete(root);
    try {
      var now = audio.context.currentTime;
      audio.gain.gain.cancelScheduledValues(now);
      audio.gain.gain.setValueAtTime(
        Math.max(audio.gain.gain.value, 0.0001),
        now
      );
      audio.gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.12);
      window.setTimeout(function () {
        try { audio.noise.stop(); } catch (error) {}
        try { audio.hum.stop(); } catch (error) {}
        try { audio.context.close(); } catch (error) {}
      }, 150);
    } catch (error) {
      try { audio.context.close(); } catch (closeError) {}
    }
  }

  async function startAmbient(root) {
    var AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) throw new Error('Web Audio unavailable');

    var context = new AudioContextCtor();
    if (context.state === 'suspended') await context.resume();

    var gain = context.createGain();
    gain.gain.value = 0.0001;
    gain.connect(context.destination);

    var filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.frequency.value = 820;
    filter.Q.value = 0.45;
    filter.connect(gain);

    var length = Math.max(1, Math.floor(context.sampleRate * 2.4));
    var buffer = context.createBuffer(1, length, context.sampleRate);
    var channel = buffer.getChannelData(0);
    for (var i = 0; i < length; i += 1) {
      var envelope = 0.7 + 0.3 * Math.sin((i / length) * Math.PI * 8);
      channel[i] = (Math.random() * 2 - 1) * envelope * 0.22;
    }

    var noise = context.createBufferSource();
    noise.buffer = buffer;
    noise.loop = true;
    noise.connect(filter);

    var hum = context.createOscillator();
    hum.type = 'sine';
    hum.frequency.value = 72;
    var humGain = context.createGain();
    humGain.gain.value = 0.012;
    hum.connect(humGain);
    humGain.connect(gain);

    noise.start();
    hum.start();

    var now = context.currentTime;
    gain.gain.exponentialRampToValueAtTime(0.055, now + 0.45);

    audioByRoot.set(root, {
      context: context,
      gain: gain,
      noise: noise,
      hum: hum
    });
  }

  function init(root) {
    if (!root || root.dataset.aquaphoriaV5UiReady === 'true') return;
    root.dataset.aquaphoriaV5UiReady = 'true';

    if (!root.dataset.aquaphoriaV5Mode) {
      root.dataset.aquaphoriaV5Mode = 'day';
    }

    var modeButton = root.querySelector('[data-aquaphoria-v5-mode-toggle]');
    var soundButton = root.querySelector('[data-aquaphoria-v5-sound-toggle]');
    var focusItems = Array.from(
      root.querySelectorAll('[data-aquaphoria-v5-focus]')
    );
    var focusCleanup = [];

    syncModeButton(root, modeButton);

    modeButton?.addEventListener('click', function () {
      var next = root.dataset.aquaphoriaV5Mode === 'night' ? 'day' : 'night';
      dispatchMode(root, next);
      syncModeButton(root, modeButton);
    });

    soundButton?.addEventListener('click', async function () {
      var on = soundButton.getAttribute('aria-pressed') === 'true';
      var label = soundButton.querySelector('[data-aquaphoria-v5-sound-label]');

      if (on) {
        stopAmbient(root);
        soundButton.setAttribute('aria-pressed', 'false');
        if (label) label.textContent = 'Ambient sound';
        return;
      }

      try {
        await startAmbient(root);
        soundButton.setAttribute('aria-pressed', 'true');
        if (label) label.textContent = 'Sound on';
      } catch (error) {
        soundButton.disabled = true;
        if (label) label.textContent = 'Sound unavailable';
      }
    });

    focusItems.forEach(function (item) {
      var focus = item.getAttribute('data-aquaphoria-v5-focus');
      if (!focus) return;

      var enter = function () {
        dispatchFocus(root, focus);
      };
      var leave = function () {
        if (root.dataset.aquaphoriaV5Focus === focus) {
          dispatchFocus(root, null);
        }
      };

      item.addEventListener('pointerenter', enter);
      item.addEventListener('focus', enter);
      item.addEventListener('pointerleave', leave);
      item.addEventListener('blur', leave);

      focusCleanup.push(function () {
        item.removeEventListener('pointerenter', enter);
        item.removeEventListener('focus', enter);
        item.removeEventListener('pointerleave', leave);
        item.removeEventListener('blur', leave);
      });
    });

    root._aquaphoriaV5Cleanup = function () {
      stopAmbient(root);
      focusCleanup.forEach(function (cleanup) {
        cleanup();
      });
      dispatchFocus(root, null);
      delete root.dataset.aquaphoriaV5UiReady;
      delete root._aquaphoriaV5Cleanup;
    };
  }

  function scan(scope) {
    var node = scope || document;
    if (node.matches && node.matches(ROOT_SELECTOR)) init(node);
    node.querySelectorAll?.(ROOT_SELECTOR).forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    }, { once: true });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var scope = event.target;
    if (!scope) return;

    var roots = [];
    if (scope.matches && scope.matches(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(function (root) {
      roots.push(root);
    });

    roots.forEach(function (root) {
      if (typeof root._aquaphoriaV5Cleanup === 'function') {
        root._aquaphoriaV5Cleanup();
      }
    });
  });

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) return;
    document.querySelectorAll(ROOT_SELECTOR).forEach(function (root) {
      var button = root.querySelector('[data-aquaphoria-v5-sound-toggle]');
      if (button?.getAttribute('aria-pressed') === 'true') {
        stopAmbient(root);
        button.setAttribute('aria-pressed', 'false');
        var label = button.querySelector('[data-aquaphoria-v5-sound-label]');
        if (label) label.textContent = 'Ambient sound';
      }
    });
  });
})();
